import { useEffect } from "react";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import API from "./services/api";

// USER PAGES
import Home from "./pages/Home";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Bet from "./pages/Bet";
import SingleBet from "./pages/SingleBet";
import GameBetPage from "./pages/GameBetPage";
import StarlineBetPage from "./pages/StarlineBetPage";
import StarlinePlayPage from "./pages/StarlinePlayPage";
import Deposit from "./pages/Deposit";
import Withdraw from "./pages/Withdraw";
import Wallet from "./pages/Wallet";
import Profile from "./pages/Profile";
import UserAccount from "./pages/UserAccount";
import ReferEarn from "./pages/ReferEarn";
import Bids from "./pages/Bids";
import Passbook from "./pages/Passbook";
import BankAccounts from "./pages/BankAccounts";
import ChartsPage from "./pages/ChartsPage";
import ChartMarketDetailPage from "./pages/ChartMarketDetailPage";
import StarlineChartPage from "./pages/StarlineChartPage";
import GameRules from "./pages/GameRules";
import UserGameRates from "./pages/UserGameRates";
import NotificationsPage from "./pages/NotificationsPage";
import LegalPage from "./pages/LegalPage";
import AviatorPage from "./pages/AviatorPage";
import CasinoPage from "./pages/CasinoPage";
import CasinoBets from "./pages/CasinoBets";
import GamesPage from "./pages/GamesPage";

// ADMIN
import AdminPanel from "./pages/AdminPanel";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminWithdraw from "./pages/AdminWithdraw";
import AdminMarkets from "./pages/AdminMarkets";
import AdminResults from "./pages/AdminResults";
import AdminSettings from "./pages/AdminSettings";
import AdminPnlStatement from "./pages/AdminPnlStatement";
import AdminLogin from "./pages/AdminLogin";
import AdminBanners from "./pages/AdminBanners";
import AdminGameRates from "./pages/AdminGameRates";
import AdminUserProfile from "./pages/AdminUserProfile";
import AdminBidRevert from "./pages/AdminBidRevert";
import AdminNotifications from "./pages/AdminNotifications";
import AdminAgents from "./pages/AdminAgents";
import AdminLeadCRM from "./pages/AdminLeadCRM";
import AdminPaymentGateway from "./pages/AdminPaymentGateway";
import AdminDeposit from "./pages/AdminDeposit";
import AdminStarline from "./pages/AdminStarline";
import AdminStarlineControl from "./pages/AdminStarlineControl";
import AdminStarlineStakeAnalysis from "./pages/AdminStarlineStakeAnalysis";
import AdminStakeAnalysis from "./pages/AdminStakeAnalysis";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import FaqPage from "./pages/FaqPage";
import HowToPlay from "./pages/HowToPlay";
import AdminBonus from "./pages/AdminBonus";
import AdminReferralBonuses from "./pages/AdminReferralBonuses";
import AdminCasino from "./pages/AdminCasino";
import AdminRiskMonitor from "./pages/AdminRiskMonitor";
import AdminSubAdmins from "./pages/AdminSubAdmins";

// AGENT
import AgentLogin from "./pages/AgentLogin";
import AgentPanel from "./pages/AgentPanel";

import { isAppWebView } from "./utils/appWebView";
import { initPushNotifications } from "./services/push";
import WhatsAppFloatBtn from "./components/WhatsAppFloatBtn";

function AppEntry() {
  if (!isAppWebView()) return <Home />;
  return getStoredUser()?.id ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
}

function getStoredUser() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (isAppWebView() && user?.id) {
      localStorage.setItem("posm_session_active", "1");
    }
    return user?.id ? user : null;
  } catch {
    return null;
  }
}

function RequireUser({ children }) {
  const user = getStoredUser();

  useEffect(() => {
    if (!user?.id) return;
    initPushNotifications(user.id).catch(() => {});
    const key = `posm_seen_${user.id}`;
    const lastPing = Number(localStorage.getItem(key) || 0);
    if (Date.now() - lastPing < 60 * 1000) return;
    localStorage.setItem(key, String(Date.now()));
    API.post(`/users/${user.id}/seen`).catch(() => {});
  }, [user?.id]);

  return user?.id ? children : <Navigate to="/login" replace />;
}

const userPage = (element) => <RequireUser>{element}</RequireUser>;

function App() {
  return (
    <BrowserRouter>
      <WhatsAppFloatBtn />
      <Routes>

        {/* PUBLIC */}
        <Route path="/" element={<AppEntry />} />
        <Route path="/login" element={<Login />} />
        <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
        <Route path="/terms-and-conditions" element={<LegalPage type="terms" />} />
        <Route path="/refund-policy" element={<LegalPage type="refund" />} />
        <Route path="/responsible-gaming" element={<LegalPage type="responsible" />} />
        <Route path="/contact-us" element={<LegalPage type="contact" />} />
        <Route path="/about-us" element={<LegalPage type="about" />} />
        <Route path="/how-to-play" element={<HowToPlay />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* USER */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/bet/:id" element={userPage(<Bet />)} />
        <Route path="/game-select" element={userPage(<Bet />)} />
        <Route path="/game-select/:id" element={userPage(<Bet />)} />
        <Route path="/single" element={userPage(<SingleBet />)} />
        <Route path="/single/:id" element={userPage(<SingleBet />)} />
        <Route path="/single-bulk" element={userPage(<GameBetPage variant="single_bulk" />)} />
        <Route path="/jodi" element={userPage(<GameBetPage variant="jodi" />)} />
        <Route path="/jodi-bulk" element={userPage(<GameBetPage variant="jodi_bulk" />)} />
        <Route path="/red-bracket" element={userPage(<GameBetPage variant="red_bracket" />)} />
        <Route path="/single-patti" element={userPage(<GameBetPage variant="sp" />)} />
        <Route path="/sp-bulk" element={userPage(<GameBetPage variant="sp_bulk" />)} />
        <Route path="/sp-motor" element={userPage(<GameBetPage variant="sp_motor" />)} />
        <Route path="/double-patti" element={userPage(<GameBetPage variant="dp" />)} />
        <Route path="/dp-bulk" element={userPage(<GameBetPage variant="dp_bulk" />)} />
        <Route path="/dp-motor" element={userPage(<GameBetPage variant="dp_motor" />)} />
        <Route path="/triple-patti" element={userPage(<GameBetPage variant="tp" />)} />
        <Route path="/half-sangam" element={userPage(<GameBetPage variant="half_sangam" />)} />
        <Route path="/full-sangam" element={userPage(<GameBetPage variant="full_sangam" />)} />
        <Route path="/starline" element={userPage(<StarlineBetPage />)} />
        <Route path="/starline/:id" element={userPage(<StarlineBetPage />)} />
        <Route path="/starline/:id/play" element={userPage(<StarlinePlayPage />)} />
        <Route path="/starline/:id/play/:mode" element={userPage(<StarlinePlayPage />)} />
        <Route path="/deposit" element={userPage(<Deposit />)} />
        <Route path="/withdraw" element={userPage(<Withdraw />)} />
        <Route path="/register" element={<Navigate to="/login?tab=register" replace />} />
        <Route path="/wallet" element={userPage(<Wallet />)} />
        <Route path="/profile" element={userPage(<Profile />)} />
        <Route path="/account" element={userPage(<UserAccount />)} />
        <Route path="/refer" element={userPage(<ReferEarn />)} />
        <Route path="/bids" element={userPage(<Bids />)} />
        <Route path="/passbook" element={userPage(<Passbook />)} />
        <Route path="/bank-accounts" element={userPage(<BankAccounts />)} />
        <Route path="/charts" element={<ChartsPage />} />
        <Route path="/charts/market/:marketId" element={<ChartMarketDetailPage />} />
        <Route path="/charts/starline/:marketId" element={<StarlineChartPage />} />
        <Route path="/game-rules" element={userPage(<GameRules />)} />
        <Route path="/game-rates" element={<UserGameRates />} />
        <Route path="/notifications" element={userPage(<NotificationsPage />)} />
        <Route path="/aviator" element={userPage(<AviatorPage />)} />
        <Route path="/casino" element={userPage(<CasinoPage />)} />
        <Route path="/casino-bets" element={userPage(<CasinoBets />)} />
        <Route path="/games" element={userPage(<GamesPage />)} />
        <Route path="/agent-login" element={<AgentLogin />} />
        <Route path="/agent-panel" element={<AgentPanel />} />

        {/* ADMIN */}
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin-dashboard" element={<AdminDashboard />} />
        <Route path="/admin-panel" element={<AdminPanel />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin/withdraw" element={<AdminWithdraw />} />
        <Route path="/admin/payouts" element={<AdminWithdraw />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/users/:id" element={<AdminUserProfile />} />
        <Route path="/admin/markets" element={<AdminMarkets />} />
        <Route path="/admin/pnl-statement" element={<AdminPnlStatement />} />
        <Route path="/admin/results" element={<AdminResults />} />
        <Route path="/admin/game-rates" element={<AdminGameRates />} />
        <Route path="/admin/bid-revert" element={<AdminBidRevert />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/payment-gateway" element={<AdminPaymentGateway />} />
        <Route path="/admin/banners" element={<AdminBanners />} />
        <Route path="/admin/notifications" element={<AdminNotifications />} />
        <Route path="/admin/agents" element={<AdminAgents />} />
        <Route path="/admin/leads" element={<AdminLeadCRM />} />
        <Route path="/admin/deposits" element={<AdminDeposit />} />
        <Route path="/admin/starline" element={<AdminStarline />} />
        <Route path="/admin/starline-control" element={<AdminStarlineControl />} />
        <Route path="/admin/starline-stake" element={<AdminStarlineStakeAnalysis />} />
        <Route path="/admin/stake-analysis" element={<AdminStakeAnalysis />} />
        <Route path="/admin/bonus" element={<AdminBonus />} />
        <Route path="/admin/referral-bonuses" element={<AdminReferralBonuses />} />
        <Route path="/admin/casino" element={<AdminCasino />} />
        <Route path="/admin/casino/settings" element={<AdminCasino />} />
        <Route path="/admin/risk-monitor" element={<AdminRiskMonitor />} />
        <Route path="/admin/sub-admins" element={<AdminSubAdmins />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
